import { useMemo } from 'react';
import { CalendarPeriod } from '@liks/lac-svc-api/esm/models/index';
import { useCostChartRange } from '../../costs/useCostChartRange';
import { useLacTenantsQuery } from '../queries/useLacTenantsQuery';
import { dayToIsoRange, monthToIsoRange } from '../../../utils/dateTimeUtils';
import { useLacCrawlJobTimeSeriesQuery } from '../queries/useLacCrawlJobTimeSeriesQuery';
import { LacCrawlJobTimeSeriesChart } from './LacCrawlJobTimeSeriesChart';

export type LacCrawlJobsTimeSeriesChartProps = {
  enabled?: boolean;
};

export function LacCrawlJobsTimeSeriesChart({
  enabled = true,
}: LacCrawlJobsTimeSeriesChartProps) {
  const range = useCostChartRange();
  const tenantsQuery = useLacTenantsQuery();
  const dailyIsoRange = dayToIsoRange(range.fromDay, range.toDay);
  const monthlyIsoRange = monthToIsoRange(range.fromMonth, range.toMonth);

  const dailyQuery = useLacCrawlJobTimeSeriesQuery({
    groupBy: CalendarPeriod.Day,
    fromDate: dailyIsoRange.fromDate,
    toDate: dailyIsoRange.toDate,
    enabled: enabled && range.mode === 'daily',
  });
  const monthlyQuery = useLacCrawlJobTimeSeriesQuery({
    groupBy: CalendarPeriod.Month,
    fromDate: monthlyIsoRange.fromDate,
    toDate: monthlyIsoRange.toDate,
    enabled: enabled && range.mode === 'monthly',
  });

  const activeQuery = range.mode === 'daily' ? dailyQuery : monthlyQuery;

  const tenantLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    for (const tenant of tenantsQuery.data ?? []) {
      labels[tenant.name] = tenant.displayName?.trim() || tenant.name;
    }
    return labels;
  }, [tenantsQuery.data]);

  return (
    <LacCrawlJobTimeSeriesChart
      mode={range.mode}
      onModeChange={range.setMode}
      fromDay={range.fromDay}
      toDay={range.toDay}
      fromMonth={range.fromMonth}
      toMonth={range.toMonth}
      rangeLabel={range.rangeLabel}
      canGoNext={range.canGoNext}
      onPrev={range.goPrev}
      onNext={range.goNext}
      query={activeQuery}
      stackedByTenant
      tenantLabels={tenantLabels}
    />
  );
}
